import { supabase } from './supabase';
import { aiRequestLimiter } from './rateLimiter';
import { logger } from './logger';

// Updated AI function call with better error handling and type safety
export async function callAIFunction(
  type: string,
  content: string,
  additionalData: any = {}
): Promise<AIResponse> {
  if (import.meta.env.DEV) {
    console.log('=== AI FUNCTION CALL START ===');
    console.log('Type:', type);
    console.log('Content length:', content.length);
    console.log('AdditionalData keys:', Object.keys(additionalData));
    if (type === 'tts') {
      console.log('=== TTS SPECIFIC LOGGING ===');
      console.log('TTS Voice from additionalData:', additionalData.voice);
      console.log('TTS Model from additionalData:', additionalData.model);
      console.log('Full additionalData:', JSON.stringify(additionalData, null, 2));
    }
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No authentication session');
    }

    if (!aiRequestLimiter.isAllowed(session.user.id)) {
      throw new Error('You\'re making requests too quickly. Please wait a moment and try again.');
    }

    const requestPayload = {
      type,
      content,
      ...additionalData
    };

    // For TTS, ensure voice and model are at the top level
    if (type === 'tts' && additionalData) {
      requestPayload.voice = additionalData.voice || 'alloy';
      requestPayload.model = additionalData.model || 'tts-1';
    }

    if (import.meta.env.DEV) {
      console.log('Request payload keys:', Object.keys(requestPayload));
      console.log('Voice in payload:', requestPayload.voice);
      console.log('Model in payload:', requestPayload.model);
      console.log('Full additionalData for TTS:', type === 'tts' ? additionalData : 'N/A');
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Missing Supabase configuration');
    }

    const url = `${supabaseUrl}/functions/v1/ai-analysis`;

    const payload = {
      type,
      content,
      ...additionalData
    };

    if (import.meta.env.DEV) {
      console.log('Making request to:', url);
      console.log('Request payload:', JSON.stringify(payload).substring(0, 200) + '...');
    }

    // Heavy operations (analyze, sermonimport) need more time with Anthropic
    const heavyTypes = ['analyze', 'sermonimport', 'translate', 'languageanalysis'];
    const timeoutMs = heavyTypes.includes(type) ? 120000 : 60000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': supabaseAnonKey
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('HTTP error:', response.status, response.statusText, errorText);

      // Better error messages based on status codes
      if (response.status === 400) {
        throw new Error(`Invalid request: ${errorText}`);
      } else if (response.status === 401) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 403) {
        throw new Error('Access denied. Please check your subscription.');
      } else if (response.status === 429) {
        try {
          const body = JSON.parse(errorText);
          throw new Error(body.error || 'Too many requests. Please try again later.');
        } catch {
          throw new Error('Too many requests. Please try again later.');
        }
      } else if (response.status >= 500) {
        throw new Error('Server error. Please try again in a moment.');
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error in callAIFunction:', error);

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return {
          success: false,
          content: '',
          error: 'Request timed out. Please try again with a shorter message.'
        };
      }

      if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        return {
          success: false,
          content: '',
          error: 'Connection failed. Please check your internet connection and try again.'
        };
      }
    }

    return {
      success: false,
      content: '',
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

export async function callAIFunctionStream(
  type: string,
  content: string,
  additionalData: any = {},
  onChunk: (chunk: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No authentication session');

  if (!aiRequestLimiter.isAllowed(session.user.id)) {
    throw new Error("You're making requests too quickly. Please wait a moment and try again.");
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !supabaseAnonKey) throw new Error('Missing Supabase configuration');

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 90000);
  // Propagate caller's abort signal into our internal controller
  if (signal) {
    signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
  }

  let response: Response;
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/ai-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ type, content, ...additionalData }),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    if (response.status === 401) throw new Error('Authentication failed. Please sign in again.');
    if (response.status === 403) {
      try { throw new Error(JSON.parse(errorText).error || 'Access denied.'); }
      catch { throw new Error('Access denied. Please check your subscription.'); }
    }
    if (response.status === 429) throw new Error('Too many requests. Please wait a moment and try again.');
    if (response.status >= 500) throw new Error('Server error. Please try again in a moment.');
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  // Graceful fallback: if the edge function returned JSON instead of a stream
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await response.json();
    if (data.error) throw new Error(data.error);
    if (data.content) onChunk(data.content);
    return;
  }

  if (!response.body) throw new Error('No response body received');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const text = decoder.decode(value, { stream: true });
      if (text) onChunk(text);
    }
  } finally {
    reader.releaseLock();
  }
}

// All AI operations are handled through Supabase Edge Functions
// This file provides helper functions for AI-related operations

interface AIResponse {
  success: boolean;
  content: string | SermonAnalysisJSON;
  error?: string;
}

export interface SermonAnalysisJSON {
  overallAssessment: {
    summary: string;
    strengths: string[];
    improvements: string[];
    score: { biblical: number; theological: number; practical: number };
  };
  biblicalExposition: string[];
  scriptureAccuracy: string[];
  theologicalSoundness: string[];
  homilecticalStructure: string[];
  illustrativeContent: string[];
  practicalApplication: string[];
  congregationConnection: string[];
  warnings: string[];
  pastoralRecommendations: string[];
  recommendedResources: string[];
}

// Secure AI API call function
async function callAIFunctionOld(type: string, payload: any): Promise<AIResponse> {
  try {
    logger.log(`Starting AI function call for type: ${type}`);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('User not authenticated');
      throw new Error('User not authenticated');
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('No session found');
      throw new Error('No session found');
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !anonKey) {
      console.error('Missing Supabase configuration');
      throw new Error('Missing Supabase configuration');
    }

    logger.log(`Making request to: ${supabaseUrl}/functions/v1/ai-analysis`);

    // --- Mobile Compatibility Fixes ---
    // The following fetch calls have been modified to improve mobile compatibility.
    // This includes reducing timeouts, adding cache-control headers, and specifying CORS options.

    if (type === 'summary') {
      const textContent = payload.content;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000); // Reduced timeout for mobile

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': anonKey,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({
          type: 'summary',
          content: textContent
        }),
        signal: controller.signal,
        mode: 'cors',
        credentials: 'include'
      });

      clearTimeout(timeoutId);
      logger.log(`Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
          console.error('Response error text:', errorText);
        } catch (e) {
          errorText = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(`AI service error (${response.status}): ${errorText}`);
      }

      let result;
      try {
        const responseText = await response.text();
        logger.log('Raw response text length:', responseText.length);
        result = JSON.parse(responseText);
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid response format from AI service');
      }

      logger.log('Parsed result success:', result.success);

      if (!result.success) {
        console.error('AI operation failed:', result.error);
        throw new Error(result.error || 'AI operation failed');
      }

      logger.log('AI function call successful');
      return {
        success: true,
        content: result.content
      };
    } else if (type === 'questions') {
      const textContent = payload.content;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000); // Reduced timeout for mobile

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': anonKey,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({
          type: 'questions',
          content: textContent
        }),
        signal: controller.signal,
        mode: 'cors',
        credentials: 'include'
      });

      clearTimeout(timeoutId);
      logger.log(`Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
          console.error('Response error text:', errorText);
        } catch (e) {
          errorText = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(`AI service error (${response.status}): ${errorText}`);
      }

      let result;
      try {
        const responseText = await response.text();
        logger.log('Raw response text length:', responseText.length);
        result = JSON.parse(responseText);
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid response format from AI service');
      }

      logger.log('Parsed result success:', result.success);

      if (!result.success) {
        console.error('AI operation failed:', result.error);
        throw new Error(result.error || 'AI operation failed');
      }

      logger.log('AI function call successful');
      return {
        success: true,
        content: result.content
      };
    } else {
      // Default handler for other types of calls, or if the above conditions aren't met.
      // You might want to replicate the logic here or call callAIFunction for these cases.
      // For now, let's assume other calls might use a default timeout.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        logger.log('Request timed out after 30 seconds');
        controller.abort();
      }, 45000); // Increase timeout to 45 seconds for analysis

      const requestBody = JSON.stringify({ type, ...payload });
      logger.log('Request payload size:', requestBody.length);

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': anonKey
        },
        body: requestBody,
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      logger.log(`Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
          console.error('Response error text:', errorText);
        } catch (e) {
          errorText = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(`AI service error (${response.status}): ${errorText}`);
      }

      let result;
      try {
        const responseText = await response.text();
        logger.log('Raw response text length:', responseText.length);
        result = JSON.parse(responseText);
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid response format from AI service');
      }

      logger.log('Parsed result success:', result.success);

      if (!result.success) {
        console.error('AI operation failed:', result.error);
        throw new Error(result.error || 'AI operation failed');
      }

      logger.log('AI function call successful');
      return {
        success: true,
        content: result.content
      };
    }
  } catch (error) {
      console.error('Error in AI function call:', error);

      // Handle AbortError specifically (from timeout)
      if (error instanceof Error && error.name === 'AbortError') {
        logger.log('Request was aborted due to timeout');
        return {
          success: false,
          content: '',
          error: 'Request timed out after 45 seconds. The AI service may be experiencing high load. Please try again in a moment.'
        };
      }

      // Handle network errors
      if (error instanceof Error && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError') ||
        error.message.includes('fetch') ||
        error.message.includes('network') ||
        error.message.includes('ECONNREFUSED') ||
        error.message.includes('ENOTFOUND')
      )) {
        logger.log('Network error detected:', error.message);
        return {
          success: false,
          content: '',
          error: 'Network connection failed. Please check your internet connection and try again.'
        };
      }

      // Handle other errors
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('AI operation failed:', errorMessage);

      return {
        success: false,
        content: '',
        error: `AI analysis failed: ${errorMessage}`
      };
    }
  }

export interface LanguageTermEntry {
  word: string;
  transliteration: string;
  strongsNumber: string;
  language: 'Hebrew' | 'Greek' | 'Aramaic';
  partOfSpeech: string;
  literalMeaning: string;
  rootMeaning: string;
  theologicalSignificance: string;
  conservativeNotes: string;
  preachingApplication: string;
  passageContext: string;
  scriptureReferences: string;
}

export interface LanguageAnalysisJSON {
  scripture: string;
  terms: LanguageTermEntry[];
  exegeticalSummary: string;
}

export async function analyzeLanguage(content: string, scripture?: string): Promise<AIResponse> {
  if (!content.trim()) {
    return { success: false, content: '', error: 'No content to analyze' };
  }
  const result = await callAIFunction('languageanalysis', content, { scripture });
  if (!result.success) {
    return { success: false, content: '', error: result.error || 'Failed to run language analysis' };
  }
  try {
    let raw = (result.content as string).trim();
    // Strip markdown fences first
    const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (fenceMatch) raw = fenceMatch[1].trim();
    // Always try to extract the outermost JSON object in case there's surrounding prose
    const objMatch = raw.match(/\{[\s\S]*\}/);
    if (objMatch) raw = objMatch[0];
    const parsed = JSON.parse(raw) as LanguageAnalysisJSON;
    return { success: true, content: parsed };
  } catch (err) {
    console.error('[analyzeLanguage] JSON parse failed:', err, '| raw preview:', (result.content as string)?.slice(0, 300));
    return { success: false, content: '', error: 'Could not parse language analysis response. Please try again.' };
  }
}

// ─── Worship Song Analysis ───────────────────────────────────────────────────

export interface SongCategoryScore {
  score: number;
  summary: string;
  evidence: string[];
}

export interface WorshipSongConcernFlag {
  flag: string;
  severity: 'low' | 'medium' | 'high';
  evidence: string;
}

export interface WorshipSongAnalysisJSON {
  songTitle: string;
  artistOrBand: string;
  overallScore: number;
  recommendation: 'Approved' | 'Use with Caution' | 'Not Recommended';
  summary: string;
  categories: {
    biblicalFaithfulness: SongCategoryScore;
    godCenteredness: SongCategoryScore;
    doctrinalClarity: SongCategoryScore;
    trinitarianClarity: SongCategoryScore;
    gospelSubstance: SongCategoryScore;
    congregationalSingability: SongCategoryScore;
  };
  concernFlags: WorshipSongConcernFlag[];
  positiveHighlights: string[];
  recommendedAlternatives?: string[];
  disclaimer: string;
}

export async function analyzeWorshipSong(
  input: { lyrics?: string; youtubeUrl?: string; songTitle?: string; artist?: string }
): Promise<AIResponse> {
  const hasInput = input.lyrics?.trim() || input.youtubeUrl?.trim() || input.songTitle?.trim();
  if (!hasInput) return { success: false, content: '', error: 'Please provide lyrics, a YouTube URL, or a song title to analyze.' };

  const content = input.lyrics?.trim()
    ? input.lyrics
    : input.songTitle
      ? `Song: ${input.songTitle}${input.artist ? ` by ${input.artist}` : ''}`
      : input.youtubeUrl || '';

  const result = await callAIFunction('worshipsong', content, {
    songTitle: input.songTitle,
    artist: input.artist,
    youtubeUrl: input.youtubeUrl,
  });
  if (!result.success) return { success: false, content: '', error: result.error || 'Failed to analyze worship song' };

  try {
    let raw = (result.content as string).trim();
    const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (fenceMatch) raw = fenceMatch[1].trim();
    const objMatch = raw.match(/\{[\s\S]*\}/);
    if (objMatch) raw = objMatch[0];
    const parsed = JSON.parse(raw) as WorshipSongAnalysisJSON;
    return { success: true, content: parsed };
  } catch (err) {
    console.error('[analyzeWorshipSong] JSON parse failed:', err, '| raw preview:', (result.content as string)?.slice(0, 300));
    return { success: false, content: '', error: 'Could not parse worship song analysis. Please try again.' };
  }
}

// ─── Speaker / Conference Vetting ────────────────────────────────────────────

export type VettingAssessment = 'No Concerns' | 'Minor Concerns' | 'Moderate Concerns' | 'Significant Concerns' | 'Insufficient Data';

export interface VettingCategory {
  assessment: VettingAssessment;
  notes: string;
  evidence?: string;
}

export interface SpeakerVettingJSON {
  subjectName: string;
  subjectType: 'Speaker' | 'Author' | 'Conference' | 'Ministry' | 'Bible Study';
  summary: string;
  recommendation: 'Generally Orthodox' | 'Use with Discernment' | 'Concerns Present' | 'Significant Concerns';
  categories: {
    viewOfScripture: VettingCategory;
    gospelClarity: VettingCategory;
    christology: VettingCategory;
    anthropology: VettingCategory;
    sexualEthics: VettingCategory;
    churchAuthority: VettingCategory;
    charismPractice: VettingCategory;
    progressiveIndicators: VettingCategory;
    prosperityIndicators: VettingCategory;
    ecumenicalConcerns: VettingCategory;
    politicalOverreach: VettingCategory;
    toneAndHumility: VettingCategory;
  };
  redFlags: string[];
  strengths: string[];
  sourcesAnalyzed: string[];
  disclaimer: string;
  generatedAt: string;
}

export async function vetSpeaker(
  input: { name: string; subjectType: string; content?: string; websiteUrl?: string }
): Promise<AIResponse> {
  if (!input.name.trim()) return { success: false, content: '', error: 'Subject name is required.' };

  const content = input.content?.trim() || input.name;
  const result = await callAIFunction('speakervetting', content, {
    name: input.name,
    subjectType: input.subjectType,
    websiteUrl: input.websiteUrl,
  });
  if (!result.success) return { success: false, content: '', error: result.error || 'Failed to generate vetting profile.' };

  try {
    let raw = (result.content as string).trim();
    const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (fenceMatch) raw = fenceMatch[1].trim();
    const objMatch = raw.match(/\{[\s\S]*\}/);
    if (objMatch) raw = objMatch[0];
    const parsed = JSON.parse(raw) as SpeakerVettingJSON;
    return { success: true, content: parsed };
  } catch (err) {
    console.error('[vetSpeaker] JSON parse failed:', err, '| raw preview:', (result.content as string)?.slice(0, 300));
    return { success: false, content: '', error: 'Could not parse vetting profile. Please try again.' };
  }
}

// ─────────────────────────────────────────────────────────────────────────────

export async function analyzeSermon(content: string, scripture?: string): Promise<AIResponse> {
  if (!content.trim()) {
    return { success: false, content: '', error: 'No content to analyze' };
  }

  const result = await callAIFunction('analyze', content, { scripture });

  if (!result.success) {
    return { success: false, content: '', error: result.error || 'Failed to analyze sermon' };
  }

  try {
    const parsed = JSON.parse(result.content as string) as SermonAnalysisJSON;
    return { success: true, content: parsed };
  } catch {
    // Backward compat: old format or non-JSON response — return raw string
    return { success: true, content: result.content };
  }
}

export async function enhanceText(text: string, enhancementType: string): Promise<AIResponse> {
  if (!text.trim()) {
    return {
      success: false,
      content: '',
      error: 'No text to enhance'
    };
  }

  return callAIFunctionOld('enhance', { content: text, enhancementType });
}

export async function generateStudyQuestions(content: string, audience: string = 'general'): Promise<AIResponse> {
  if (!content.trim()) {
    return {
      success: false,
      content: '',
      error: 'No content to analyze'
    };
  }

  return callAIFunction('questions', content, { audience });
}

export async function generateSermonSummary(content: string): Promise<AIResponse> {
  if (!content.trim()) {
    return {
      success: false,
      content: '',
      error: 'No content to summarize'
    };
  }

  return callAIFunction('summary', content, {
    tone: 'pastoral',
    style: 'engaging',
    instructions: 'Write a warm, pastoral summary that captures the heart of the message in an engaging and relational way. Use conversational language that invites reflection and emphasizes God\'s love and practical guidance for living. Avoid formal or academic tone.'
  });
}

export async function fetchAIScripture(prompt: string, translation: string = 'KJV'): Promise<{ reference: string; text: string; version: string }> {
  if (!prompt.trim()) {
    throw new Error('No prompt provided');
  }

  const result = await callAIFunctionOld('scripture', { prompt, translation });

  if (!result.success) {
    throw new Error(result.error || 'Failed to fetch scripture');
  }

  try {
    return JSON.parse(result.content);
  } catch (error) {
    throw new Error('Failed to parse scripture response');
  }
}

export async function askTheologicalAssistant(message: string, conversationHistory: any[] = []): Promise<AIResponse> {
  if (!message.trim()) {
    return {
      success: false,
      content: '',
      error: 'No message provided'
    };
  }

  return callAIFunctionOld('theologicalAssistant', { content: message, conversationHistory });
}